import React from "react";

import Button from "@busbud/design-system-components/dist/Button";
import Card from "@busbud/design-system-components/dist/Card";
import Hidden from "@busbud/design-system-components/dist/Hidden";
import BusbudCoinLG from "@busbud/design-system-components/dist/Icons/BusbudCoin/LG";
import BusbudCoinMD from "@busbud/design-system-components/dist/Icons/BusbudCoin/MD";
import Label from "@busbud/design-system-components/dist/Label";
import Stack from "@busbud/design-system-components/dist/Stack";

import {
  isDynamicWhiteLabel,
  useWhitelabelContext
} from "@app/context/whitelabel-context";
import { useAppContext } from "@app/helpers/hooks";
import type { CurrencyCode } from "@app/types/currency";
import type { LoyaltyTierStatus } from "@app/types/loyalty";

import { useStyles } from "./user-credit-balance.styles";
import { LocalizedPrice } from "../localized-price";

export interface ReduxProps {
  balance: number;
  currency: CurrencyCode;
  tier_status?: LoyaltyTierStatus;
}

export interface ReduxActions {
  openActivityModal: () => void;
}

interface Props {
  emphase_credit?: boolean;
  hide_level?: boolean;
  show_activity_button?: boolean;
}

export const UserCreditBalance: React.FC<Props & ReduxProps & ReduxActions> = ({
  balance,
  currency,
  emphase_credit = false,
  openActivityModal,
  show_activity_button = false
}) => {
  const { translator } = useAppContext();
  const { whitelabel } = useWhitelabelContext();
  const { classes } = useStyles();
  const is_dynamic_whitelabel: boolean = isDynamicWhiteLabel(whitelabel);

  if (is_dynamic_whitelabel || !balance || !isFinite(balance)) {
    return null;
  }

  return (
    <Card className={classes.container}>
      <Stack spacing={1.5} direction="column">
        <Stack spacing={0.5} alignItems="center">
          <Hidden mdUp>
            <BusbudCoinMD color="palette.green.regular.700" />
          </Hidden>
          <Hidden smDown>
            <BusbudCoinLG color="palette.green.regular.700" />
          </Hidden>

          <Stack spacing={3} flexWrap="wrap" justifyContent="space-between">
            <Label
              size="lg"
              fontWeight="bold"
              color="palette.green.regular.700"
            >
              {translator.t("!loyalty.credit-balance")}
            </Label>
            <Label
              size="xl"
              fontWeight={emphase_credit ? "heavy" : "bold"}
              color="palette.green.regular.700"
            >
              <LocalizedPrice price_in_cents={balance} currency={currency} />
            </Label>
          </Stack>
        </Stack>
        {!!show_activity_button && (
          <Button
            fullWidth
            size="md"
            color="tertiary"
            onClick={() => openActivityModal()}
          >
            {translator.t("!loyalty.view-activity")}
          </Button>
        )}
      </Stack>
    </Card>
  );
};
